/**
 * Banks Directory Page
 * Comprehensive list of Banks & Banking Institutions worldwide
 * Users can do global banking, exchange and market trades with ParachPay Card
 */
import { useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Landmark, 
  Building2, 
  Globe, 
  Search, 
  ExternalLink, 
  MapPin,
  CreditCard,
  TrendingUp,
  ArrowRightLeft,
  Wallet
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Comprehensive Banks Directory Data
const banksDirectory = {
  "North America": {
    "United States": {
      banks: [
        { name: "JPMorgan Chase & Co.", city: "New York, NY", address: "383 Madison Avenue, New York, NY 10179", url: "https://www.jpmorganchase.com", type: "bank", category: "Investment & Commercial" },
        { name: "Bank of America", city: "Charlotte, NC", address: "100 North Tryon Street, Charlotte, NC 28255", url: "https://www.bankofamerica.com", type: "bank", category: "Commercial" },
        { name: "Wells Fargo", city: "San Francisco, CA", address: "420 Montgomery Street, San Francisco, CA 94104", url: "https://www.wellsfargo.com", type: "bank", category: "Commercial" },
        { name: "Citibank", city: "New York, NY", address: "388 Greenwich Street, New York, NY 10013", url: "https://www.citigroup.com", type: "bank", category: "Investment & Commercial" },
        { name: "Goldman Sachs", city: "New York, NY", address: "200 West Street, New York, NY 10282", url: "https://www.goldmansachs.com", type: "bank", category: "Investment" },
        { name: "Morgan Stanley", city: "New York, NY", address: "1585 Broadway, New York, NY 10036", url: "https://www.morganstanley.com", type: "bank", category: "Investment" },
        { name: "U.S. Bank", city: "Minneapolis, MN", address: "800 Nicollet Mall, Minneapolis, MN 55402", url: "https://www.usbank.com", type: "bank", category: "Commercial" },
        { name: "PNC Financial Services", city: "Pittsburgh, PA", address: "The Tower at PNC Plaza, 300 Fifth Avenue, Pittsburgh, PA 15222", url: "https://www.pnc.com", type: "bank", category: "Commercial" },
        { name: "Charles Schwab", city: "Westlake, TX", address: "211 Main Street, San Francisco, CA 94105", url: "https://www.schwab.com", type: "bank", category: "Investment" },
        { name: "TD Bank", city: "Cherry Hill, NJ", address: "1701 Route 70 East, Cherry Hill, NJ 08034", url: "https://www.td.com", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Federal Reserve Bank of New York", city: "New York, NY", address: "33 Liberty Street, New York, NY 10045", url: "https://www.newyorkfed.org", type: "branch", category: "Central Bank" },
        { name: "Federal Reserve Bank of San Francisco", city: "San Francisco, CA", address: "101 Market Street, San Francisco, CA 94105", url: "https://www.frbsf.org", type: "branch", category: "Central Bank" }
      ]
    },
    "Canada": {
      banks: [
        { name: "Royal Bank of Canada (RBC)", city: "Toronto, ON", address: "200 Bay Street, Royal Bank Plaza, Toronto, ON M5J 2J5", url: "https://www.rbc.com", type: "bank", category: "Commercial" },
        { name: "Toronto-Dominion Bank (TD)", city: "Toronto, ON", address: "66 Wellington Street West, Toronto, ON M5K 1A2", url: "https://www.td.com", type: "bank", category: "Commercial" },
        { name: "Bank of Nova Scotia (Scotiabank)", city: "Toronto, ON", address: "44 King Street West, Toronto, ON M5H 1H1", url: "https://www.scotiabank.com", type: "bank", category: "Commercial" },
        { name: "Bank of Montreal (BMO)", city: "Montreal, QC", address: "129 Rue Saint-Jacques, Montreal, QC H2Y 1L6", url: "https://www.bmo.com", type: "bank", category: "Commercial" },
        { name: "Canadian Imperial Bank of Commerce (CIBC)", city: "Toronto, ON", address: "81 Bay Street, Toronto, ON M5J 0E7", url: "https://www.cibc.com", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Bank of Canada", city: "Ottawa, ON", address: "234 Wellington Street, Ottawa, ON K1A 0G9", url: "https://www.bankofcanada.ca", type: "branch", category: "Central Bank" }
      ]
    },
    "Mexico": {
      banks: [
        { name: "BBVA México", city: "Mexico City", address: "Paseo de la Reforma 510, Col. Juárez, 06600 CDMX", url: "https://www.bbva.mx", type: "bank", category: "Commercial" },
        { name: "Banorte", city: "Monterrey", address: "Av. Revolución 3000, Col. Primavera, Monterrey, N.L.", url: "https://www.banorte.com", type: "bank", category: "Commercial" },
        { name: "Citibanamex", city: "Mexico City", address: "Av. Roberto Medellín 800, Santa Fe, CDMX", url: "https://www.banamex.com", type: "bank", category: "Commercial" },
        { name: "Santander México", city: "Mexico City", address: "Prolongación Paseo de la Reforma 500, Santa Fe, CDMX", url: "https://www.santander.com.mx", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Banco de México", city: "Mexico City", address: "Av. 5 de Mayo 2, Centro Histórico, 06000 CDMX", url: "https://www.banxico.org.mx", type: "branch", category: "Central Bank" }
      ]
    }
  },
  "Europe": {
    "United Kingdom": {
      banks: [
        { name: "HSBC Holdings", city: "London", address: "8 Canada Square, Canary Wharf, London E14 5HQ", url: "https://www.hsbc.com", type: "bank", category: "Commercial & Investment" },
        { name: "Barclays", city: "London", address: "1 Churchill Place, Canary Wharf, London E14 5HP", url: "https://www.barclays.co.uk", type: "bank", category: "Commercial & Investment" },
        { name: "Lloyds Banking Group", city: "London", address: "25 Gresham Street, London EC2V 7HN", url: "https://www.lloydsbankinggroup.com", type: "bank", category: "Commercial" },
        { name: "NatWest Group", city: "Edinburgh", address: "36 St Andrew Square, Edinburgh EH2 2YB", url: "https://www.natwestgroup.com", type: "bank", category: "Commercial" },
        { name: "Standard Chartered", city: "London", address: "1 Basinghall Avenue, London EC2V 5DD", url: "https://www.sc.com", type: "bank", category: "Commercial & Investment" },
        { name: "Santander UK", city: "London", address: "2 Triton Square, Regent's Place, London NW1 3AN", url: "https://www.santander.co.uk", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Bank of England", city: "London", address: "Threadneedle Street, London EC2R 8AH", url: "https://www.bankofengland.co.uk", type: "branch", category: "Central Bank" }
      ]
    },
    "Germany": {
      banks: [
        { name: "Deutsche Bank", city: "Frankfurt", address: "Taunusanlage 12, 60325 Frankfurt am Main", url: "https://www.db.com", type: "bank", category: "Investment & Commercial" },
        { name: "Commerzbank", city: "Frankfurt", address: "Kaiserplatz, 60311 Frankfurt am Main", url: "https://www.commerzbank.de", type: "bank", category: "Commercial" },
        { name: "DZ Bank", city: "Frankfurt", address: "Platz der Republik 6, 60325 Frankfurt", url: "https://www.dzbank.de", type: "bank", category: "Cooperative" },
        { name: "KfW Bank", city: "Frankfurt", address: "Palmengartenstraße 5-9, 60325 Frankfurt", url: "https://www.kfw.de", type: "bank", category: "Development" },
        { name: "Landesbank Baden-Württemberg", city: "Stuttgart", address: "Am Hauptbahnhof 2, 70173 Stuttgart", url: "https://www.lbbw.de", type: "bank", category: "Regional" }
      ],
      branches: [
        { name: "Deutsche Bundesbank", city: "Frankfurt", address: "Wilhelm-Epstein-Straße 14, 60431 Frankfurt", url: "https://www.bundesbank.de", type: "branch", category: "Central Bank" },
        { name: "European Central Bank (ECB)", city: "Frankfurt", address: "Sonnemannstraße 20, 60314 Frankfurt", url: "https://www.ecb.europa.eu", type: "branch", category: "Central Bank" }
      ]
    },
    "France": {
      banks: [
        { name: "BNP Paribas", city: "Paris", address: "16 Boulevard des Italiens, 75009 Paris", url: "https://www.bnpparibas.com", type: "bank", category: "Commercial & Investment" },
        { name: "Crédit Agricole", city: "Montrouge", address: "12 Place des États-Unis, 92127 Montrouge", url: "https://www.credit-agricole.com", type: "bank", category: "Cooperative" },
        { name: "Société Générale", city: "Paris", address: "29 Boulevard Haussmann, 75009 Paris", url: "https://www.societegenerale.com", type: "bank", category: "Commercial & Investment" },
        { name: "Groupe BPCE", city: "Paris", address: "50 Avenue Pierre Mendès France, 75013 Paris", url: "https://www.groupebpce.com", type: "bank", category: "Cooperative" },
        { name: "Natixis", city: "Paris", address: "30 Avenue Pierre Mendès France, 75013 Paris", url: "https://www.natixis.com", type: "bank", category: "Investment" }
      ],
      branches: [
        { name: "Banque de France", city: "Paris", address: "31 Rue Croix des Petits Champs, 75001 Paris", url: "https://www.banque-france.fr", type: "branch", category: "Central Bank" }
      ]
    },
    "Netherlands": {
      banks: [
        { name: "ING Group", city: "Amsterdam", address: "Bijlmerdreef 106, 1102 CT Amsterdam", url: "https://www.ing.com", type: "bank", category: "Commercial" },
        { name: "ABN AMRO", city: "Amsterdam", address: "Gustav Mahlerlaan 10, 1082 PP Amsterdam", url: "https://www.abnamro.com", type: "bank", category: "Commercial" },
        { name: "Rabobank", city: "Utrecht", address: "Croeselaan 18, 3521 CB Utrecht", url: "https://www.rabobank.com", type: "bank", category: "Cooperative" }
      ],
      branches: [
        { name: "De Nederlandsche Bank", city: "Amsterdam", address: "Westeinde 1, 1017 ZN Amsterdam", url: "https://www.dnb.nl", type: "branch", category: "Central Bank" }
      ]
    },
    "Switzerland": {
      banks: [
        { name: "UBS Group", city: "Zurich", address: "Bahnhofstrasse 45, 8001 Zürich", url: "https://www.ubs.com", type: "bank", category: "Investment & Private" },
        { name: "Credit Suisse (Now UBS)", city: "Zurich", address: "Paradeplatz 8, 8001 Zürich", url: "https://www.credit-suisse.com", type: "bank", category: "Investment & Private" },
        { name: "Julius Baer", city: "Zurich", address: "Bahnhofstrasse 36, 8001 Zürich", url: "https://www.juliusbaer.com", type: "bank", category: "Private" },
        { name: "Pictet Group", city: "Geneva", address: "Route des Acacias 60, 1211 Geneva", url: "https://www.pictet.com", type: "bank", category: "Private" }
      ],
      branches: [
        { name: "Swiss National Bank", city: "Bern", address: "Bundesplatz 1, 3003 Bern", url: "https://www.snb.ch", type: "branch", category: "Central Bank" }
      ]
    },
    "Spain": {
      banks: [
        { name: "Banco Santander", city: "Santander", address: "Paseo de Pereda 9-12, 39004 Santander", url: "https://www.santander.com", type: "bank", category: "Commercial" },
        { name: "BBVA", city: "Bilbao", address: "Plaza de San Nicolás 4, 48005 Bilbao", url: "https://www.bbva.com", type: "bank", category: "Commercial" },
        { name: "CaixaBank", city: "Valencia", address: "Calle Pintor Sorolla 2-4, 46002 Valencia", url: "https://www.caixabank.com", type: "bank", category: "Commercial" },
        { name: "Banco Sabadell", city: "Sabadell", address: "Plaça de Sant Roc 20, 08201 Sabadell", url: "https://www.bancsabadell.com", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Banco de España", city: "Madrid", address: "Calle de Alcalá 48, 28014 Madrid", url: "https://www.bde.es", type: "branch", category: "Central Bank" }
      ]
    },
    "Italy": {
      banks: [
        { name: "Intesa Sanpaolo", city: "Turin", address: "Piazza San Carlo 156, 10121 Torino", url: "https://www.intesasanpaolo.com", type: "bank", category: "Commercial" },
        { name: "UniCredit", city: "Milan", address: "Piazza Gae Aulenti 3, 20154 Milano", url: "https://www.unicredit.eu", type: "bank", category: "Commercial" },
        { name: "Mediobanca", city: "Milan", address: "Piazzetta Enrico Cuccia 1, 20121 Milano", url: "https://www.mediobanca.com", type: "bank", category: "Investment" },
        { name: "Banca Monte dei Paschi di Siena", city: "Siena", address: "Piazza Salimbeni 3, 53100 Siena", url: "https://www.mps.it", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Banca d'Italia", city: "Rome", address: "Via Nazionale 91, 00184 Roma", url: "https://www.bancaditalia.it", type: "branch", category: "Central Bank" }
      ]
    }
  },
  "Asia": {
    "Japan": {
      banks: [
        { name: "Mitsubishi UFJ Financial Group (MUFG)", city: "Tokyo", address: "2-7-1 Marunouchi, Chiyoda-ku, Tokyo 100-8330", url: "https://www.mufg.jp", type: "bank", category: "Commercial" },
        { name: "Sumitomo Mitsui Financial Group", city: "Tokyo", address: "1-2 Marunouchi 1-chome, Chiyoda-ku, Tokyo 100-0005", url: "https://www.smfg.co.jp", type: "bank", category: "Commercial" },
        { name: "Mizuho Financial Group", city: "Tokyo", address: "1-5-5 Otemachi, Chiyoda-ku, Tokyo 100-8176", url: "https://www.mizuho-fg.com", type: "bank", category: "Commercial" },
        { name: "Nomura Holdings", city: "Tokyo", address: "1-13-1 Nihonbashi, Chuo-ku, Tokyo 103-8645", url: "https://www.nomura.com", type: "bank", category: "Investment" },
        { name: "Daiwa Securities", city: "Tokyo", address: "9-1 Marunouchi 1-chome, Chiyoda-ku, Tokyo 100-6752", url: "https://www.daiwa.jp", type: "bank", category: "Investment" }
      ],
      branches: [
        { name: "Bank of Japan", city: "Tokyo", address: "2-1-1 Nihonbashi-Hongokucho, Chuo-ku, Tokyo 103-8660", url: "https://www.boj.or.jp", type: "branch", category: "Central Bank" }
      ]
    },
    "China": {
      banks: [
        { name: "Industrial and Commercial Bank of China (ICBC)", city: "Beijing", address: "55 Fuxingmennei Avenue, Xicheng District, Beijing 100140", url: "https://www.icbc.com.cn", type: "bank", category: "Commercial" },
        { name: "China Construction Bank", city: "Beijing", address: "25 Finance Street, Xicheng District, Beijing 100033", url: "https://www.ccb.com", type: "bank", category: "Commercial" },
        { name: "Agricultural Bank of China", city: "Beijing", address: "69 Jianguomennei Avenue, Dongcheng District, Beijing", url: "https://www.abchina.com", type: "bank", category: "Commercial" },
        { name: "Bank of China", city: "Beijing", address: "1 Fuxingmen Nei Dajie, Beijing 100818", url: "https://www.boc.cn", type: "bank", category: "Commercial" },
        { name: "China Merchants Bank", city: "Shenzhen", address: "7088 Shennan Boulevard, Futian District, Shenzhen", url: "https://www.cmbchina.com", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "People's Bank of China", city: "Beijing", address: "32 Chengfang Street, Xicheng District, Beijing 100800", url: "http://www.pbc.gov.cn", type: "branch", category: "Central Bank" }
      ]
    },
    "Singapore": {
      banks: [
        { name: "DBS Bank", city: "Singapore", address: "12 Marina Boulevard, DBS Asia Central, Singapore 018982", url: "https://www.dbs.com", type: "bank", category: "Commercial" },
        { name: "Oversea-Chinese Banking Corporation (OCBC)", city: "Singapore", address: "63 Chulia Street, OCBC Centre East, Singapore 049514", url: "https://www.ocbc.com", type: "bank", category: "Commercial" },
        { name: "United Overseas Bank (UOB)", city: "Singapore", address: "80 Raffles Place, UOB Plaza, Singapore 048624", url: "https://www.uob.com.sg", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Monetary Authority of Singapore (MAS)", city: "Singapore", address: "10 Shenton Way, MAS Building, Singapore 079117", url: "https://www.mas.gov.sg", type: "branch", category: "Central Bank" }
      ]
    },
    "Hong Kong": {
      banks: [
        { name: "HSBC Hong Kong", city: "Hong Kong", address: "1 Queen's Road Central, Hong Kong", url: "https://www.hsbc.com.hk", type: "bank", category: "Commercial" },
        { name: "Bank of China (Hong Kong)", city: "Hong Kong", address: "1 Garden Road, Central, Hong Kong", url: "https://www.bochk.com", type: "bank", category: "Commercial" },
        { name: "Hang Seng Bank", city: "Hong Kong", address: "83 Des Voeux Road Central, Hong Kong", url: "https://www.hangseng.com", type: "bank", category: "Commercial" },
        { name: "Standard Chartered Hong Kong", city: "Hong Kong", address: "Standard Chartered Tower, 388 Kwun Tong Road", url: "https://www.sc.com/hk", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Hong Kong Monetary Authority", city: "Hong Kong", address: "55/F, Two International Finance Centre, 8 Finance Street", url: "https://www.hkma.gov.hk", type: "branch", category: "Central Bank" }
      ]
    },
    "India": {
      banks: [
        { name: "State Bank of India", city: "Mumbai", address: "State Bank Bhavan, Madame Cama Road, Mumbai 400021", url: "https://www.sbi.co.in", type: "bank", category: "Commercial" },
        { name: "HDFC Bank", city: "Mumbai", address: "HDFC Bank House, Senapati Bapat Marg, Lower Parel, Mumbai", url: "https://www.hdfcbank.com", type: "bank", category: "Commercial" },
        { name: "ICICI Bank", city: "Mumbai", address: "ICICI Bank Tower, Bandra Kurla Complex, Mumbai", url: "https://www.icicibank.com", type: "bank", category: "Commercial" },
        { name: "Axis Bank", city: "Mumbai", address: "Axis House, Bombay Dyeing Mills Compound, Mumbai", url: "https://www.axisbank.com", type: "bank", category: "Commercial" },
        { name: "Kotak Mahindra Bank", city: "Mumbai", address: "27BKC, Plot C-27, G Block, Bandra Kurla Complex, Mumbai", url: "https://www.kotak.com", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Reserve Bank of India", city: "Mumbai", address: "Central Office, Shahid Bhagat Singh Marg, Mumbai 400001", url: "https://www.rbi.org.in", type: "branch", category: "Central Bank" }
      ]
    },
    "United Arab Emirates": {
      banks: [
        { name: "Emirates NBD", city: "Dubai", address: "Baniyas Road, Deira, Dubai", url: "https://www.emiratesnbd.com", type: "bank", category: "Commercial" },
        { name: "First Abu Dhabi Bank (FAB)", city: "Abu Dhabi", address: "FAB Building, Khalifa Street, Abu Dhabi", url: "https://www.bankfab.com", type: "bank", category: "Commercial" },
        { name: "Abu Dhabi Commercial Bank (ADCB)", city: "Abu Dhabi", address: "ADCB Tower, Mohammed Bin Zayed City, Abu Dhabi", url: "https://www.adcb.com", type: "bank", category: "Commercial" },
        { name: "Mashreq Bank", city: "Dubai", address: "Mashreq Bank HQ, Omar Bin Al Khattab Road, Dubai", url: "https://www.mashreqbank.com", type: "bank", category: "Commercial" },
        { name: "Dubai Islamic Bank", city: "Dubai", address: "Dubai Islamic Bank Building, Al Maktoum Road, Dubai", url: "https://www.dib.ae", type: "bank", category: "Islamic" }
      ],
      branches: [
        { name: "Central Bank of the UAE", city: "Abu Dhabi", address: "King Abdullah Bin Abdulaziz Al Saud Street, Abu Dhabi", url: "https://www.centralbank.ae", type: "branch", category: "Central Bank" }
      ]
    },
    "South Korea": {
      banks: [
        { name: "KB Kookmin Bank", city: "Seoul", address: "84 Namdaemun-ro, Jung-gu, Seoul", url: "https://www.kbstar.com", type: "bank", category: "Commercial" },
        { name: "Shinhan Bank", city: "Seoul", address: "20 Sejong-daero 9-gil, Jung-gu, Seoul", url: "https://www.shinhan.com", type: "bank", category: "Commercial" },
        { name: "Hana Bank", city: "Seoul", address: "66 Eulji-ro, Jung-gu, Seoul", url: "https://www.hanabank.com", type: "bank", category: "Commercial" },
        { name: "Woori Bank", city: "Seoul", address: "51 Sogong-ro, Jung-gu, Seoul", url: "https://www.wooribank.com", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Bank of Korea", city: "Seoul", address: "67 Sejong-daero, Jung-gu, Seoul 04514", url: "https://www.bok.or.kr", type: "branch", category: "Central Bank" }
      ]
    }
  },
  "Middle East": {
    "Saudi Arabia": {
      banks: [
        { name: "Saudi National Bank (SNB)", city: "Riyadh", address: "King Abdulaziz Road, Riyadh 11411", url: "https://www.snb.com", type: "bank", category: "Commercial" },
        { name: "Al Rajhi Bank", city: "Riyadh", address: "Olaya Street, Riyadh 11411", url: "https://www.alrajhibank.com.sa", type: "bank", category: "Islamic" },
        { name: "Riyad Bank", city: "Riyadh", address: "King Abdulaziz Road, Riyadh", url: "https://www.riyadbank.com", type: "bank", category: "Commercial" },
        { name: "Banque Saudi Fransi", city: "Riyadh", address: "King Fahad Road, Riyadh", url: "https://www.alfransi.com.sa", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Saudi Central Bank (SAMA)", city: "Riyadh", address: "King Saud Bin Abdulaziz Road, Riyadh 11169", url: "https://www.sama.gov.sa", type: "branch", category: "Central Bank" }
      ]
    },
    "Qatar": {
      banks: [
        { name: "Qatar National Bank (QNB)", city: "Doha", address: "QNB Head Office, Ali Bin Abdullah Street, Doha", url: "https://www.qnb.com", type: "bank", category: "Commercial" },
        { name: "Qatar Islamic Bank", city: "Doha", address: "Grand Hamad Street, Doha", url: "https://www.qib.com.qa", type: "bank", category: "Islamic" },
        { name: "Commercial Bank of Qatar", city: "Doha", address: "Commercial Bank Plaza, D-Ring Road, Doha", url: "https://www.cbq.qa", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Qatar Central Bank", city: "Doha", address: "Corniche Street, West Bay, Doha", url: "https://www.qcb.gov.qa", type: "branch", category: "Central Bank" }
      ]
    },
    "Israel": {
      banks: [
        { name: "Bank Hapoalim", city: "Tel Aviv", address: "50 Rothschild Boulevard, Tel Aviv 6688312", url: "https://www.bankhapoalim.com", type: "bank", category: "Commercial" },
        { name: "Bank Leumi", city: "Tel Aviv", address: "35 Yehuda Halevi Street, Tel Aviv", url: "https://www.leumi.co.il", type: "bank", category: "Commercial" },
        { name: "Israel Discount Bank", city: "Tel Aviv", address: "27-31 Yehuda Halevi Street, Tel Aviv", url: "https://www.discountbank.co.il", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Bank of Israel", city: "Jerusalem", address: "2 Kaplan Street, Kiryat Ben-Gurion, Jerusalem", url: "https://www.boi.org.il", type: "branch", category: "Central Bank" }
      ]
    },
    "Turkey": {
      banks: [
        { name: "İş Bankası", city: "Istanbul", address: "İş Kuleleri, Levent, 34330 İstanbul", url: "https://www.isbank.com.tr", type: "bank", category: "Commercial" },
        { name: "Garanti BBVA", city: "Istanbul", address: "Levent Nispetiye Mah, Aytar Cad. 2, 34340 İstanbul", url: "https://www.garantibbva.com.tr", type: "bank", category: "Commercial" },
        { name: "Akbank", city: "Istanbul", address: "Sabancı Center, Levent, 34330 İstanbul", url: "https://www.akbank.com", type: "bank", category: "Commercial" },
        { name: "Yapı Kredi", city: "Istanbul", address: "Yapı Kredi Plaza, Levent, 34330 İstanbul", url: "https://www.yapikredi.com.tr", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Central Bank of Turkey", city: "Ankara", address: "Anafartalar Mah, İstiklal Cad. 10, 06050 Ankara", url: "https://www.tcmb.gov.tr", type: "branch", category: "Central Bank" }
      ]
    }
  },
  "Africa": {
    "South Africa": {
      banks: [
        { name: "Standard Bank", city: "Johannesburg", address: "5 Simmonds Street, Johannesburg 2001", url: "https://www.standardbank.co.za", type: "bank", category: "Commercial" },
        { name: "FirstRand Bank", city: "Johannesburg", address: "4 Merchant Place, Corner Fredman Drive & Rivonia Road", url: "https://www.firstrand.co.za", type: "bank", category: "Commercial" },
        { name: "Absa Group", city: "Johannesburg", address: "15 Troye Street, Johannesburg 2001", url: "https://www.absa.co.za", type: "bank", category: "Commercial" },
        { name: "Nedbank", city: "Johannesburg", address: "135 Rivonia Road, Sandown, Johannesburg", url: "https://www.nedbank.co.za", type: "bank", category: "Commercial" },
        { name: "Investec", city: "Johannesburg", address: "100 Grayston Drive, Sandown, Sandton", url: "https://www.investec.com", type: "bank", category: "Investment" }
      ],
      branches: [
        { name: "South African Reserve Bank", city: "Pretoria", address: "370 Helen Joseph Street, Pretoria 0002", url: "https://www.resbank.co.za", type: "branch", category: "Central Bank" }
      ]
    },
    "Nigeria": {
      banks: [
        { name: "First Bank of Nigeria", city: "Lagos", address: "Samuel Asabia House, 35 Marina, Lagos", url: "https://www.firstbanknigeria.com", type: "bank", category: "Commercial" },
        { name: "Zenith Bank", city: "Lagos", address: "Plot 84, Ajose Adeogun Street, Victoria Island, Lagos", url: "https://www.zenithbank.com", type: "bank", category: "Commercial" },
        { name: "Access Bank", city: "Lagos", address: "Access Tower, 999c Danmole Street, Victoria Island, Lagos", url: "https://www.accessbankplc.com", type: "bank", category: "Commercial" },
        { name: "Guaranty Trust Bank (GTBank)", city: "Lagos", address: "Plot 635, Akin Adesola Street, Victoria Island, Lagos", url: "https://www.gtbank.com", type: "bank", category: "Commercial" },
        { name: "United Bank for Africa (UBA)", city: "Lagos", address: "UBA House, 57 Marina, Lagos", url: "https://www.ubagroup.com", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Central Bank of Nigeria", city: "Abuja", address: "Plot 33, Abubakar Tafawa Balewa Way, Central Business District, Abuja", url: "https://www.cbn.gov.ng", type: "branch", category: "Central Bank" }
      ]
    },
    "Egypt": {
      banks: [
        { name: "National Bank of Egypt", city: "Cairo", address: "1187 Corniche El Nil, Cairo", url: "https://www.nbe.com.eg", type: "bank", category: "Commercial" },
        { name: "Banque Misr", city: "Cairo", address: "153 Mohamed Farid Street, Cairo", url: "https://www.banquemisr.com", type: "bank", category: "Commercial" },
        { name: "Commercial International Bank (CIB)", city: "Cairo", address: "Nile Tower, 21/23 Charles de Gaulle Street, Cairo", url: "https://www.cibeg.com", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Central Bank of Egypt", city: "Cairo", address: "54 El Gomhoreya Street, Downtown, Cairo", url: "https://www.cbe.org.eg", type: "branch", category: "Central Bank" }
      ]
    },
    "Kenya": {
      banks: [
        { name: "Kenya Commercial Bank (KCB)", city: "Nairobi", address: "KCB Towers, Kencom House, Moi Avenue, Nairobi", url: "https://www.kcbgroup.com", type: "bank", category: "Commercial" },
        { name: "Equity Bank", city: "Nairobi", address: "Equity Centre, Hospital Road, Upper Hill, Nairobi", url: "https://www.equitygroupholdings.com", type: "bank", category: "Commercial" },
        { name: "Co-operative Bank of Kenya", city: "Nairobi", address: "Co-operative House, Haile Selassie Avenue, Nairobi", url: "https://www.co-opbank.co.ke", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Central Bank of Kenya", city: "Nairobi", address: "Haile Selassie Avenue, Nairobi 60000", url: "https://www.centralbank.go.ke", type: "branch", category: "Central Bank" }
      ]
    },
    "Morocco": {
      banks: [
        { name: "Attijariwafa Bank", city: "Casablanca", address: "2 Boulevard Moulay Youssef, Casablanca", url: "https://www.attijariwafabank.com", type: "bank", category: "Commercial" },
        { name: "Banque Populaire du Maroc", city: "Casablanca", address: "101 Boulevard Zerktouni, Casablanca", url: "https://www.gbp.ma", type: "bank", category: "Commercial" },
        { name: "BMCE Bank of Africa", city: "Casablanca", address: "140 Avenue Hassan II, Casablanca", url: "https://www.bankofafrica.ma", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Bank Al-Maghrib", city: "Rabat", address: "277 Avenue Mohammed V, Rabat", url: "https://www.bkam.ma", type: "branch", category: "Central Bank" }
      ]
    }
  },
  "Oceania": {
    "Australia": {
      banks: [
        { name: "Commonwealth Bank of Australia", city: "Sydney", address: "Tower 1, 201 Sussex Street, Sydney NSW 2000", url: "https://www.commbank.com.au", type: "bank", category: "Commercial" },
        { name: "Westpac Banking Corporation", city: "Sydney", address: "275 Kent Street, Sydney NSW 2000", url: "https://www.westpac.com.au", type: "bank", category: "Commercial" },
        { name: "Australia and New Zealand Banking Group (ANZ)", city: "Melbourne", address: "833 Collins Street, Docklands VIC 3008", url: "https://www.anz.com.au", type: "bank", category: "Commercial" },
        { name: "National Australia Bank (NAB)", city: "Melbourne", address: "800 Bourke Street, Docklands VIC 3008", url: "https://www.nab.com.au", type: "bank", category: "Commercial" },
        { name: "Macquarie Group", city: "Sydney", address: "50 Martin Place, Sydney NSW 2000", url: "https://www.macquarie.com", type: "bank", category: "Investment" }
      ],
      branches: [
        { name: "Reserve Bank of Australia", city: "Sydney", address: "65 Martin Place, Sydney NSW 2000", url: "https://www.rba.gov.au", type: "branch", category: "Central Bank" }
      ]
    },
    "New Zealand": {
      banks: [
        { name: "ANZ New Zealand", city: "Auckland", address: "ANZ Centre, 23-29 Albert Street, Auckland 1010", url: "https://www.anz.co.nz", type: "bank", category: "Commercial" },
        { name: "ASB Bank", city: "Auckland", address: "12 Jellicoe Street, Auckland 1010", url: "https://www.asb.co.nz", type: "bank", category: "Commercial" },
        { name: "Westpac New Zealand", city: "Auckland", address: "188 Quay Street, Auckland 1010", url: "https://www.westpac.co.nz", type: "bank", category: "Commercial" },
        { name: "BNZ (Bank of New Zealand)", city: "Wellington", address: "80 Queen Street, Auckland 1010", url: "https://www.bnz.co.nz", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Reserve Bank of New Zealand", city: "Wellington", address: "2 The Terrace, Wellington 6011", url: "https://www.rbnz.govt.nz", type: "branch", category: "Central Bank" }
      ]
    }
  },
  "South America": {
    "Brazil": {
      banks: [
        { name: "Banco do Brasil", city: "Brasília", address: "SBS Quadra 1, Bloco G, Brasília, DF 70073-901", url: "https://www.bb.com.br", type: "bank", category: "Commercial" },
        { name: "Itaú Unibanco", city: "São Paulo", address: "Praça Alfredo Egydio de Souza Aranha 100, São Paulo", url: "https://www.itau.com.br", type: "bank", category: "Commercial" },
        { name: "Bradesco", city: "Osasco", address: "Cidade de Deus, Osasco, SP 06029-900", url: "https://www.bradesco.com.br", type: "bank", category: "Commercial" },
        { name: "Santander Brasil", city: "São Paulo", address: "Av. Presidente Juscelino Kubitschek 2041, São Paulo", url: "https://www.santander.com.br", type: "bank", category: "Commercial" },
        { name: "BTG Pactual", city: "São Paulo", address: "Av. Brigadeiro Faria Lima 3477, São Paulo", url: "https://www.btgpactual.com", type: "bank", category: "Investment" }
      ],
      branches: [
        { name: "Banco Central do Brasil", city: "Brasília", address: "SBS Quadra 3, Bloco B, Brasília, DF 70074-900", url: "https://www.bcb.gov.br", type: "branch", category: "Central Bank" }
      ]
    },
    "Argentina": {
      banks: [
        { name: "Banco de la Nación Argentina", city: "Buenos Aires", address: "Bartolomé Mitre 326, C1036AAH Buenos Aires", url: "https://www.bna.com.ar", type: "bank", category: "Commercial" },
        { name: "Banco Santander Río", city: "Buenos Aires", address: "Bartolomé Mitre 480, Buenos Aires", url: "https://www.santander.com.ar", type: "bank", category: "Commercial" },
        { name: "Banco Galicia", city: "Buenos Aires", address: "Tte. Gral. J. D. Perón 407, Buenos Aires", url: "https://www.galicia.com.ar", type: "bank", category: "Commercial" },
        { name: "BBVA Argentina", city: "Buenos Aires", address: "Av. Córdoba 111, Buenos Aires", url: "https://www.bbva.com.ar", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Banco Central de la República Argentina", city: "Buenos Aires", address: "Reconquista 266, C1003ABF Buenos Aires", url: "https://www.bcra.gob.ar", type: "branch", category: "Central Bank" }
      ]
    },
    "Chile": {
      banks: [
        { name: "Banco de Chile", city: "Santiago", address: "Paseo Ahumada 251, Santiago", url: "https://www.bancochile.cl", type: "bank", category: "Commercial" },
        { name: "Banco Santander Chile", city: "Santiago", address: "Bandera 140, Santiago", url: "https://www.santander.cl", type: "bank", category: "Commercial" },
        { name: "BCI (Banco de Crédito e Inversiones)", city: "Santiago", address: "El Golf 125, Las Condes, Santiago", url: "https://www.bci.cl", type: "bank", category: "Commercial" },
        { name: "Banco Estado", city: "Santiago", address: "Av. Libertador Bernardo O'Higgins 1111, Santiago", url: "https://www.bancoestado.cl", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Banco Central de Chile", city: "Santiago", address: "Agustinas 1180, Santiago", url: "https://www.bcentral.cl", type: "branch", category: "Central Bank" }
      ]
    },
    "Colombia": {
      banks: [
        { name: "Bancolombia", city: "Medellín", address: "Carrera 48 No. 26-85, Medellín", url: "https://www.bancolombia.com", type: "bank", category: "Commercial" },
        { name: "Banco de Bogotá", city: "Bogotá", address: "Calle 36 No. 7-47, Bogotá", url: "https://www.bancodebogota.com", type: "bank", category: "Commercial" },
        { name: "Davivienda", city: "Bogotá", address: "Av. El Dorado No. 68C-61, Bogotá", url: "https://www.davivienda.com", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Banco de la República", city: "Bogotá", address: "Carrera 7 No. 14-78, Bogotá", url: "https://www.banrep.gov.co", type: "branch", category: "Central Bank" }
      ]
    },
    "Peru": {
      banks: [
        { name: "Banco de Crédito del Perú (BCP)", city: "Lima", address: "Calle Centenario 156, La Molina, Lima", url: "https://www.viabcp.com", type: "bank", category: "Commercial" },
        { name: "BBVA Perú", city: "Lima", address: "Av. República de Panamá 3055, San Isidro, Lima", url: "https://www.bbva.pe", type: "bank", category: "Commercial" },
        { name: "Interbank", city: "Lima", address: "Av. Carlos Villarán 140, La Victoria, Lima", url: "https://www.interbank.pe", type: "bank", category: "Commercial" }
      ],
      branches: [
        { name: "Banco Central de Reserva del Perú", city: "Lima", address: "Jr. Santa Rosa 441-445, Lima", url: "https://www.bcrp.gob.pe", type: "branch", category: "Central Bank" }
      ]
    }
  }
};

const BanksDirectory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContinent, setSelectedContinent] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const continents = Object.keys(banksDirectory);

  const filteredEntries = useMemo(() => {
    let entries = [];
    
    const continentsToSearch = selectedContinent === 'all' 
      ? continents 
      : [selectedContinent];
    
    continentsToSearch.forEach(continent => {
      const countries = banksDirectory[continent];
      Object.entries(countries).forEach(([country, data]) => {
        const allItems = [...(data.banks || []), ...(data.branches || [])];
        allItems.forEach(item => {
          entries.push({
            ...item,
            country,
            continent
          });
        });
      });
    });
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      entries = entries.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.city.toLowerCase().includes(query) ||
        item.country.toLowerCase().includes(query) ||
        item.continent.toLowerCase().includes(query) ||
        (item.category && item.category.toLowerCase().includes(query))
      );
    }
    
    if (filterType !== 'all') {
      entries = entries.filter(item => item.type === filterType);
    }
    
    return entries;
  }, [searchQuery, selectedContinent, filterType, continents]);

  const stats = useMemo(() => {
    let totalBanks = 0;
    let totalBranches = 0;
    let totalCountries = 0;
    
    Object.values(banksDirectory).forEach(countries => {
      totalCountries += Object.keys(countries).length;
      Object.values(countries).forEach(data => {
        totalBanks += (data.banks || []).length;
        totalBranches += (data.branches || []).length;
      });
    });
    
    return { totalBanks, totalBranches, totalCountries, totalContinents: continents.length };
  }, [continents]);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Investment': return 'bg-purple-500/10 text-purple-600';
      case 'Commercial': return 'bg-blue-500/10 text-blue-600';
      case 'Central Bank': return 'bg-amber-500/10 text-amber-600';
      case 'Islamic': return 'bg-emerald-500/10 text-emerald-600';
      case 'Private': return 'bg-rose-500/10 text-rose-600';
      default: return 'bg-gray-500/10 text-gray-600';
    }
  };

  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="banks-directory-page"
      >
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-sm font-medium mb-4">
            <Landmark className="w-4 h-4" />
            Global Banking with ParachPay Card
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-4">
            Banks & Banking Institutions Directory
          </h1>
          <p className="text-lg text-muted-foreground">
            Access banks and financial institutions worldwide. Trade, exchange currencies, and manage global markets using your Merchant ParachPay Card.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <Landmark className="w-8 h-8 mx-auto text-amber-600 mb-2" />
              <p className="text-3xl font-bold">{stats.totalBanks}</p>
              <p className="text-sm text-muted-foreground">Banks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Building2 className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
              <p className="text-3xl font-bold">{stats.totalBranches}</p>
              <p className="text-sm text-muted-foreground">Central Banks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Globe className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold">{stats.totalCountries}</p>
              <p className="text-sm text-muted-foreground">Countries</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <TrendingUp className="w-8 h-8 mx-auto text-purple-600 mb-2" />
              <p className="text-3xl font-bold">24/7</p>
              <p className="text-sm text-muted-foreground">Market Access</p>
            </CardContent>
          </Card>
        </div>

        {/* Banking Services Banner */}
        <Card className="bg-gradient-to-r from-amber-600 to-amber-700 text-white border-0">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  <Landmark className="w-10 h-10" />
                  <ArrowRightLeft className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Global Banking & Trading</h3>
                  <p className="text-amber-100">Use your Merchant ParachPay Card for currency exchange, market trades, and international banking</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Link 
                  to="/travel-directory"
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-2 rounded-full transition-colors text-sm"
                >
                  <Globe className="w-4 h-4" />
                  Airlines
                </Link>
                <Link 
                  to="/hotels-directory"
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-2 rounded-full transition-colors text-sm"
                >
                  <Wallet className="w-4 h-4" />
                  Hotels
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search banks, countries, cities, or categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filterType === 'all' ? 'default' : 'outline'}
              onClick={() => setFilterType('all')}
              className="rounded-xl"
            >
              All
            </Button>
            <Button
              variant={filterType === 'bank' ? 'default' : 'outline'}
              onClick={() => setFilterType('bank')}
              className="rounded-xl"
            >
              <Landmark className="w-4 h-4 mr-2" />
              Banks
            </Button>
            <Button
              variant={filterType === 'branch' ? 'default' : 'outline'}
              onClick={() => setFilterType('branch')}
              className="rounded-xl"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Central Banks
            </Button>
          </div>
        </div>

        {/* Continent Tabs */}
        <Tabs value={selectedContinent} onValueChange={setSelectedContinent} className="w-full">
          <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0 mb-6">
            <TabsTrigger 
              value="all" 
              className="rounded-xl data-[state=active]:bg-amber-600 data-[state=active]:text-white"
            >
              All Regions
            </TabsTrigger>
            {continents.map(continent => (
              <TabsTrigger 
                key={continent} 
                value={continent}
                className="rounded-xl data-[state=active]:bg-amber-600 data-[state=active]:text-white"
              >
                {continent}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Results */}
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredEntries.length} results
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEntries.map((item, idx) => (
                <motion.div
                  key={`${item.name}-${idx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.02 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          {item.type === 'bank' ? (
                            <div className="p-2 rounded-lg bg-amber-500/10">
                              <Landmark className="w-5 h-5 text-amber-600" />
                            </div>
                          ) : (
                            <div className="p-2 rounded-lg bg-emerald-500/10">
                              <Building2 className="w-5 h-5 text-emerald-600" />
                            </div>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {item.type === 'bank' ? 'Bank' : 'Central Bank'}
                          </Badge>
                        </div>
                        <Badge className="bg-muted text-muted-foreground text-xs">
                          {item.country}
                        </Badge>
                      </div>
                      
                      <h3 className="font-semibold text-lg mb-1">{item.name}</h3>
                      
                      {item.category && (
                        <Badge className={`${getCategoryColor(item.category)} text-xs mb-2`}>
                          {item.category}
                        </Badge>
                      )}
                      
                      <div className="space-y-2 text-sm text-muted-foreground mb-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{item.city}</span>
                        </div>
                        <p className="text-xs pl-6">{item.address}</p>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        className="w-full rounded-xl"
                        asChild
                      >
                        <a 
                          href={item.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Visit Website
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            
            {filteredEntries.length === 0 && (
              <Card className="text-center py-12">
                <CardContent>
                  <Globe className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No results found</p>
                  <p className="text-sm text-muted-foreground">Try adjusting your search or filters</p>
                </CardContent>
              </Card>
            )}
          </div>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default BanksDirectory;
